/**
 * Platform Detection Utility
 *
 * Single source of truth for detecting platform type, OS, and browser.
 * Replaces scattered WebView detection logic across the codebase.
 */

export interface PlatformInfo {
  type: 'desktop' | 'mobile' | 'webview';
  os: 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';
  browser: 'safari' | 'chrome' | 'firefox' | 'edge' | 'webview' | 'unknown';
  isMobile: boolean;
  isWebView: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
}

/**
 * Detects if the current environment is a WebView (Capacitor or other embedded browser)
 */
function detectWebView(): boolean {
  const ua = navigator.userAgent;

  // Check for React Native WebView
  if (typeof (window as any).ReactNativeWebView !== 'undefined') {
    return true;
  }

  // Check for iOS WebView (no Safari in UA string)
  if (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua)) {
    return true;
  }

  // Check for Android WebView
  if (/wv|Version\/[\d.]+.*Chrome\/[.0-9]*/.test(ua)) {
    return true;
  }

  // Check for Capacitor
  if (typeof (window as any).Capacitor !== 'undefined') {
    return true;
  }

  return false;
}

/**
 * Detects the operating system
 */
function detectOS(): PlatformInfo['os'] {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'ios';
  }

  if (/Android/.test(ua)) {
    return 'android';
  }

  if (/Mac/.test(platform)) {
    return 'macos';
  }

  if (/Win/.test(platform)) {
    return 'windows';
  }

  if (/Linux/.test(platform)) {
    return 'linux';
  }

  return 'unknown';
}

/**
 * Detects the browser
 */
function detectBrowser(isWebView: boolean): PlatformInfo['browser'] {
  if (isWebView) {
    return 'webview';
  }

  const ua = navigator.userAgent;

  if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    return 'safari';
  }

  if (/Chrome/.test(ua)) {
    return 'chrome';
  }

  if (/Firefox/.test(ua)) {
    return 'firefox';
  }

  if (/Edg/.test(ua)) {
    return 'edge';
  }

  return 'unknown';
}

/**
 * Detects if the current device is mobile
 */
function detectMobile(): boolean {
  // Check screen width (mobile breakpoint)
  if (window.innerWidth < 768) {
    return true;
  }

  // Check user agent for mobile devices
  const ua = navigator.userAgent;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }

  // Check for touch support
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    // But not all touch devices are mobile (e.g., touch laptops)
    // So also check screen size
    return window.innerWidth < 1024;
  }

  return false;
}

/**
 * Main platform detection function
 * Call this once and cache the result for the session
 */
export function detectPlatform(): PlatformInfo {
  const isWebView = detectWebView();
  const os = detectOS();
  const browser = detectBrowser(isWebView);
  const isMobile = detectMobile();
  const isIOS = os === 'ios';
  const isAndroid = os === 'android';
  const isDesktop = !isMobile;

  // Determine platform type
  let type: PlatformInfo['type'];
  if (isWebView) {
    type = 'webview';
  } else if (isMobile) {
    type = 'mobile';
  } else {
    type = 'desktop';
  }

  return {
    type,
    os,
    browser,
    isMobile,
    isWebView,
    isIOS,
    isAndroid,
    isDesktop,
  };
}

/**
 * Determines if the platform requires a user gesture for media playback
 * iOS and some mobile browsers require user interaction before playing video
 */
export function requiresUserGesture(platform: PlatformInfo): boolean {
  // iOS always requires user gesture for autoplay
  if (platform.isIOS) {
    return true;
  }

  // Android WebView might require user gesture
  if (platform.isAndroid && platform.isWebView) {
    return true;
  }

  // Mobile Safari requires user gesture
  if (platform.isMobile && platform.browser === 'safari') {
    return true;
  }

  return false;
}

/**
 * Cached platform info - call detectPlatform() once per session
 */
let cachedPlatformInfo: PlatformInfo | null = null;

/**
 * Gets the cached platform info or detects it if not cached
 */
export function getPlatformInfo(): PlatformInfo {
  if (!cachedPlatformInfo) {
    cachedPlatformInfo = detectPlatform();
  }
  return cachedPlatformInfo;
}

/**
 * Clears the cached platform info (useful for testing)
 */
export function clearPlatformCache(): void {
  cachedPlatformInfo = null;
}
